/**
 * Stock Remnant Inventory Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as stockService from '../api/services/stock.service';
import type {
  StockItemCreateRequest,
  StockItemUpdateRequest,
} from '../types';
import { toast } from '../stores/toast.store';

const KEYS = {
  items: ['stock', 'items'] as const,
  item: (id: number) => ['stock', 'item', id] as const,
  options: ['stock', 'options'] as const,
};

interface UseStockItemsParams {
  offset?: number;
  limit?: number;
  status?: string;
  brand_code?: string;
  search?: string;
  warehouse_location?: string;
}

/**
 * Hook for fetching paginated stock items (with keepPreviousData for pagination)
 */
export function useStockItems(params: UseStockItemsParams = {}) {
  return useQuery({
    queryKey: [...KEYS.items, params],
    queryFn: async () => {
      const result = await stockService.getStockItems(params);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch stock items');
      }
      return result.data!;
    },
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook for fetching a single stock item detail
 */
export function useStockItem(id: number | undefined) {
  return useQuery({
    queryKey: KEYS.item(id!),
    queryFn: async () => {
      const result = await stockService.getStockItem(id!);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch stock item');
      }
      return result.data!;
    },
    enabled: !!id,
  });
}

/**
 * Hook for fetching stock dropdown options
 */
export function useStockOptions() {
  return useQuery({
    queryKey: KEYS.options,
    queryFn: async () => {
      const result = await stockService.getStockOptions();
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to fetch stock options');
      }
      return result.data!;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for creating a stock item
 */
export function useCreateStockItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: StockItemCreateRequest) => {
      const result = await stockService.createStockItem(data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to create stock item');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.items });
      toast.success('新增成功', `餘料 ${data.code} 已建立`);
    },
    onError: (error: Error) => {
      toast.error('新增失敗', error.message);
    },
  });
}

/**
 * Hook for updating a stock item
 */
export function useUpdateStockItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: StockItemUpdateRequest;
    }) => {
      const result = await stockService.updateStockItem(id, data);
      if (!result.success) {
        throw new Error(result.error?.message || 'Failed to update stock item');
      }
      return result.data!;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: KEYS.items });
      queryClient.invalidateQueries({ queryKey: KEYS.item(data.id) });
      toast.success('更新成功');
    },
    onError: (error: Error) => {
      toast.error('更新失敗', error.message);
    },
  });
}
